import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { RequireAuth, RequirePermission } from './auth/Can';
import { ToastProvider } from './components/ui/Overlay';
import { PreferencesProvider } from './hooks/usePreferences';
import { AppShell } from './components/layout/AppShell';
import { EmptyState } from './components/ui/AsyncBoundary';

import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Vehicles } from './pages/Vehicles';
import { VehicleDetail } from './pages/VehicleDetail';
import { Devices, DeviceDetail } from './pages/Devices';
import { Firmware, RolloutDetail } from './pages/Firmware';
import { Alerts } from './pages/Alerts';
import { Analytics } from './pages/Analytics';
import { Admin } from './pages/Admin';
import { Audit } from './pages/Audit';
import { Platform } from './pages/Platform';
import { Settings } from './pages/Settings';

/**
 * Routes carry the same permission strings as the sidebar, so what a person
 * can navigate to and what they can see in the nav cannot drift apart. Both
 * are still only a courtesy — the API refuses independently.
 */
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PreferencesProvider>
        <ToastProvider>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="vehicles/:id" element={<VehicleDetail />} />

              <Route
                path="devices"
                element={
                  <RequirePermission permission="device:read">
                    <Devices />
                  </RequirePermission>
                }
              />
              <Route
                path="devices/:id"
                element={
                  <RequirePermission permission="device:read">
                    <DeviceDetail />
                  </RequirePermission>
                }
              />

              <Route
                path="firmware"
                element={
                  <RequirePermission permission="device:read">
                    <Firmware />
                  </RequirePermission>
                }
              />
              <Route
                path="firmware/rollouts/:id"
                element={
                  <RequirePermission permission="device:read">
                    <RolloutDetail />
                  </RequirePermission>
                }
              />

              {/* Every role has an account to manage and access to ask for,
                  so this route carries no permission gate. */}
              <Route path="settings" element={<Settings />} />

              <Route
                path="alerts"
                element={
                  <RequirePermission permission="alert:read">
                    <Alerts />
                  </RequirePermission>
                }
              />
              <Route
                path="analytics"
                element={
                  <RequirePermission permission="analytics:read">
                    <Analytics />
                  </RequirePermission>
                }
              />
              <Route
                path="admin"
                element={
                  <RequirePermission permission="user:read">
                    <Admin />
                  </RequirePermission>
                }
              />
              <Route
                path="platform"
                element={
                  <RequirePermission permission="org:write">
                    <Platform />
                  </RequirePermission>
                }
              />
              <Route
                path="audit"
                element={
                  <RequirePermission permission="audit:read">
                    <Audit />
                  </RequirePermission>
                }
              />

              <Route
                path="*"
                element={
                  <EmptyState
                    icon="search"
                    title="That page does not exist"
                    description="Check the address, or head back to the dashboard."
                  />
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ToastProvider>
        </PreferencesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
